// eslint-disable-next-line
import Prism from 'prismjs';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-ini';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-regex';
import 'prismjs/components/prism-powershell';
import 'prismjs/components/prism-docker';
import 'prismjs/components/prism-git';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-sql';

import { FluidIconKey, FluidSeverity, FluidSize } from '@lmig/fluid-core';
import { Component, ComponentInterface, h, Prop, State } from '@stencil/core';
import { timer } from 'rxjs';

/**
 * @displayName Code Block
 * @contextData Syntax highlighter
 */
@Component({
  tag: 'fluid-code-block',
  styleUrl: 'fluid-code-block.less',
  shadow: true,
})
export class FluidCodeBlock implements ComponentInterface {
  /**
   * The code snippet you want to highlight, as a string
   */
  @Prop({ mutable: true }) code: string;

  /**
   * If true will render without the default container (copy functionality)
   */
  @Prop({ mutable: true }) noContainer: boolean;

  /**
   * The name of the language definition.
   * Check for the full list of supported languages here: https://prismjs.com/#supported-languages
   * imported languages: javascript, java, ini, jsx, yaml, python, json, typescript, bash, regex, powershell, docker, git, c#, sql
   * @default
   * @type
   */
  @Prop({ mutable: true }) type = 'js';

  /**
   * If you want to add a custom class name (CSS).
   * This will be only added when the noContainer property is set to true
   */
  @Prop({ mutable: true }) inlineType: string;

  /**
   * If false the copy button will not be displayed
   */
  @Prop({ mutable: true }) copyable = true;

  /**
   * If true the code will be in muted style
   */
  @Prop({ mutable: true }) muted: boolean;

  /**
   * Message to display in the fluid-alert when the contents have been copied to the clipboard.
   */
  @Prop({ mutable: true }) copyMessage = 'Copied!';

  @State() copiedAlert;

  /**
   * Returns a highlighted HTML string
   */
  renderCode() {
    const type = this.type === 'json' ? 'js' : this.type;
    return Prism.highlight(this.code, Prism.languages[type], type);
  }

  renderCopyIcon() {
    if (this.copyable) {
      return (
        <fluid-icon
          class={'copy-icon'}
          size={FluidSize.SMALL}
          iconKey={FluidIconKey.COPY}
          onClick={() => this.copy()}
        />
      );
    }
  }

  renderCodeBlock() {
    const highlightClass = {
      [`language-${this.type || 'json'}`]: true,
      muted: this.muted,
    };
    return (
      <pre>
        <code class={highlightClass} innerHTML={this.renderCode()} />
      </pre>
    );
  }

  render() {
    const inlineClass = {
      inline: true,
      [this.inlineType]: !!this.inlineType,
    };
    return this.noContainer ? (
      <code class={inlineClass}>{this.renderCodeBlock()}</code>
    ) : (
      <div class='code-wrapper'>
        {this.renderCopyIcon()}
        {this.renderCodeBlock()}
        {this.renderCopiedAlert()}
      </div>
    );
  }

  renderCopiedAlert() {
    if (this.copiedAlert) {
      return (
        <fluid-alert
          severity={FluidSeverity.SUCCESS}
          message={this.copyMessage}
          inline={false}
          visible={true}
        />
      );
    }
  }

  copy() {
    navigator.clipboard.writeText(this.code).then(() => {
      this.copiedAlert = true;
      timer(2000).subscribe(() => {
        this.copiedAlert = false;
      });
    });
  }
}
